import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const DEFAULT_STATUSES = [
  { id: 'open', label: 'Open', color: '#ef4444', order: 0 },
  { id: 'in_progress', label: 'In Progress', color: '#f59e0b', order: 1 },
  { id: 'done', label: 'Done', color: '#22c55e', order: 2 },
];

function useToDoConfig(companyId) {
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initializedRef = useRef(false);

  const statusesRef = useMemo(() => {
    if (!companyId) return null;
    return collection(db, 'user_data', companyId, 'todo_config', 'config', 'statuses');
  }, [companyId]);

  const categoriesRef = useMemo(() => {
    if (!companyId) return null;
    return collection(db, 'user_data', companyId, 'todo_config', 'config', 'categories');
  }, [companyId]);

  useEffect(() => {
    initializedRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (!statusesRef || !categoriesRef) {
      setStatuses([]);
      setCategories([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);

    const unsubStatuses = onSnapshot(
      statusesRef,
      async (snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStatuses(items);
        setLoading(false);

        if (!initializedRef.current && items.length === 0) {
          initializedRef.current = true;
          try {
            await Promise.all(
              DEFAULT_STATUSES.map((s) =>
                setDoc(doc(db, 'user_data', companyId, 'todo_config', 'config', 'statuses', s.id), {
                  label: s.label,
                  color: s.color,
                  order: s.order,
                })
              )
            );
          } catch (err) {
            console.error(err);
            setError(err);
          }
        }
      },
      (err) => {
        console.error(err);
        setError(err);
        setLoading(false);
      }
    );

    const unsubCategories = onSnapshot(
      categoriesRef,
      (snapshot) => {
        setCategories(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error(err);
        setError(err);
      }
    );

    return () => {
      unsubStatuses();
      unsubCategories();
    };
  }, [companyId, statusesRef, categoriesRef]);

  const upsertStatus = useCallback(
    async ({ id, label, color, order }) => {
      if (!companyId || !statusesRef) return null;
      try {
        setError(null);
        if (id) {
          await setDoc(
            doc(db, 'user_data', companyId, 'todo_config', 'config', 'statuses', id),
            { label, color, order },
            { merge: true }
          );
          return id;
        }
        const res = await addDoc(statusesRef, { label, color, order });
        return res.id;
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId, statusesRef]
  );

  const deleteStatus = useCallback(
    async (statusId) => {
      if (!companyId || !statusId) return;
      try {
        setError(null);
        await deleteDoc(doc(db, 'user_data', companyId, 'todo_config', 'config', 'statuses', statusId));
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId]
  );

  const upsertCategory = useCallback(
    async ({ id, label, color, order }) => {
      if (!companyId || !categoriesRef) return null;
      try {
        setError(null);
        if (id) {
          await setDoc(
            doc(db, 'user_data', companyId, 'todo_config', 'config', 'categories', id),
            { label, color, order },
            { merge: true }
          );
          return id;
        }
        const res = await addDoc(categoriesRef, { label, color, order });
        return res.id;
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId, categoriesRef]
  );

  const deleteCategory = useCallback(
    async (categoryId) => {
      if (!companyId || !categoryId) return;
      try {
        setError(null);
        await deleteDoc(doc(db, 'user_data', companyId, 'todo_config', 'config', 'categories', categoryId));
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId]
  );

  return {
    statuses,
    categories,
    loading,
    error,
    upsertStatus,
    deleteStatus,
    upsertCategory,
    deleteCategory,
  };
}

export default useToDoConfig;

